import React, { useEffect, useState } from 'react';
import { array, bool, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { get, isEmpty } from 'lodash';

import Paper from '@material-ui/core/Paper';

import CancelButton from '../../../components/CancelButton';
import ConfirmButton from '../../../components/ConfirmButton';

import Appointments from './Appointments';
import CallInformation from './CallInformation';
import DeleteServiceCallModal from '../../Modals/DeleteServiceCallModal';
import EditServiceCallModal from '../../Modals/EditServiceCallModal';
import Filters from './Filters';
import FiltersDrawer from './FiltersDrawer';
import NewAppointmentModal from '../../Modals/NewAppointmentModal';
import CreateServiceRequestModal from '../../Modals/CreateServiceRequestModal';
import Overview from './Overview';
import ServiceItemContextMenu from './ServiceItemContextMenu';
import ServiceItemList from './ServiceItemList';

import * as CoreActions from '../../../actions/coreActions';
import * as ServiceManagementActions from '../../../actions/serviceManagementActions';

import classes from './ManagementPage.module.css';

const ManagementPage = ({
    activeItem,
    appointmentStatuses,
    callStatus,
    isItemActive,
    isLoading,
    priority,
    resetActiveCall,
    saveActiveCall,
    sendServiceCallNotification,
    setProgressBarLoading,
    targetClient,
    ...props
}) => {

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [newAppointmentModalOpen, setNewAppointmentModalOpen] = useState(false);
    const [editServiceCallModalOpen, setEditServiceCallModalOpen] = useState(false);
    const [createServiceCallModalOpen, setCreateServiceCallModalOpen] = useState(false);
    const [disableSave, setDisableSave] = useState(false);

    useEffect(() => {
        const disabled = isEmpty(callStatus) || isEmpty(priority) || isEmpty(targetClient);

        setDisableSave(disabled);
    }, []);

    useEffect(() => {
        const disabled = isEmpty(callStatus) || isEmpty(priority) || isEmpty(targetClient);

        setDisableSave(disabled);
    }, [callStatus, priority, targetClient]);

    const handleCancel = async () => {
        setProgressBarLoading(true);
        await resetActiveCall();
        setProgressBarLoading(false);
    };

    const handleSave = async notify => {
        setProgressBarLoading(true);
        await saveActiveCall({notify});
        setProgressBarLoading(false);
    };

    const renderNames = () => {
        const clientName = get(activeItem, 'relatedClient.name', null);
        const siteName = get(activeItem, 'relatedSite.name', null);

        if (clientName && siteName) {
            return `${clientName}, ${siteName}`;
        }

        return clientName;
    };

    const renderEquipmentOrSystem = () => {
        const equipmentName = get(activeItem, 'relatedEquipment.name', null);
        const systemName = get(activeItem, 'relatedSystem.name', null);

        // Will only ever be one or the other so...
        if (equipmentName) return `, ${equipmentName}`;
        if (systemName) return `, ${systemName}`;

        return;
    };

    const renderDetails = () => {
        const {
            locked,
            workOrder
        } = activeItem;

        return (
            <div className={classes.detailWrapper}>
                <div className={classes.detailArea}>
                    <div className={classes.detailControl}>
                        <span className={classes.detailTitle}>
                            {
                                activeItem.locked &&
                                <img src="/assets/images/lockFlat.svg" alt="locked" />
                            }
                            WO-{workOrder} | {renderNames()}{renderEquipmentOrSystem()}
                        </span>
                        <span className={classes.detailContextMenuIcon}>
                            <ServiceItemContextMenu
                                onDeleteModalOpen={setDeleteModalOpen}
                                onNewAppointmentModalOpen={setNewAppointmentModalOpen}
                                onEditServiceCallModalOpen={setEditServiceCallModalOpen}
                                sendServiceCallNotification={sendServiceCallNotification}
                                activeItem={activeItem}
                            />
                        </span>
                    </div>
                    <div className={classes.detailBoxes}>
                        <div className={classes.leftDetailBoxes}>
                            <Overview />
                            <Appointments />
                        </div>
                        <CallInformation />
                    </div>
                </div>
                <div className={classes.buttonArea}>
                    <CancelButton
                        cancel={handleCancel}
                    />
                    <ConfirmButton
                        label="Save"
                        onClick={() => handleSave(false)}
                        isLoading={isLoading}
                        disabled={disableSave || locked}
                    />
                    {
                        process.env.advancedCommEnabled &&
                        <ConfirmButton
                            label="Save & Notify"
                            onClick={() => handleSave(true)}
                            isLoading={isLoading}
                            disabled={disableSave || locked}
                        />
                    }
                </div>
            </div>
        );
    };

    const renderBlankSlate = () => {
        return (
            <div className={classes.blankState}>
                <div className={classes.imageContainer}>
                    <img src="/assets/images/legoBatmanIconColor.svg" alt="batman icon" />
                    <p>Let's get these calls handled!</p>
                </div>
            </div>
        );
    };

    const { workOrder } = activeItem;

    return (
        <React.Fragment>
            <div className={classes.ManagementPage}>
                <Paper className={classes.filterArea}>
                    <Filters
                        onCreateServiceCallModalOpen={setCreateServiceCallModalOpen}
                    />
                </Paper>
                <Paper className={classes.contentArea}>
                    <Paper
                        className={classes.listArea}
                    >
                        <ServiceItemList />
                    </Paper>
                    {
                        isItemActive ? renderDetails() : renderBlankSlate()
                    }
                </Paper>
            </div>
            <FiltersDrawer />
            <DeleteServiceCallModal
                open={deleteModalOpen}
                close={() => setDeleteModalOpen(false)}
                title="Delete Service Request"
            />
            <EditServiceCallModal
                open={editServiceCallModalOpen}
                close={() => setEditServiceCallModalOpen(false)}
                title="Edit Service Request"
            />
            <NewAppointmentModal
                open={newAppointmentModalOpen}
                close={() => setNewAppointmentModalOpen(false)}
                title={`WO-${workOrder}: New Appointment`}
                appointmentStatuses={appointmentStatuses}
            />
            <CreateServiceRequestModal
                open={createServiceCallModalOpen}
                close={() => setCreateServiceCallModalOpen(false)}
                title="Create Service Request"
            />
        </React.Fragment>
    );
};

ManagementPage.defaultProps = {
    activeItem:          {},
    appointmentStatuses: [],
    callStatus:          '',
    isItemActive:        false,
    isLoading:           false,
    priority:            '',
    targetClient:        {}
};

ManagementPage.propTypes = {
    activeItem:          object,
    appointmentStatuses: array,
    callStatus:          string,
    isItemActive:        bool,
    isLoading:           bool,
    priority:            string,
    targetClient:        object
};

const mapStateToProps = state => {
    return {
        activeItem:          state.svcManagement.activeItem,
        appointmentStatuses: state.svcManagement.appointmentStatuses,
        callStatus:          state.svcManagement.callStatus,
        isItemActive:        state.svcManagement.isItemActive,
        isLoading:           state.ui.isLoading,
        priority:            state.svcManagement.priority,
        targetClient:        state.svcManagement.targetClient
    };
};

const mapDispatchToProps = {
    ...CoreActions,
    ...ServiceManagementActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ManagementPage);
